import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { useContext, useMemo, useState } from "react";
import { useRunSftpOutboundReportMutation } from "store/queries/sftpPartners";
import { ISftpOutboundReport, ISftpPartnerDetail } from "types/SftpPartner";

import {
  isoDateToLocalDate,
  lastReportedWeekUtc,
  localDateToIsoDate,
  outboundFileName,
  sftpApiErrorMessage,
  yesterdayUtc,
} from "../../utils/sftpPartners";

interface ISendNowDialogProps {
  partner: ISftpPartnerDetail;
  report: ISftpOutboundReport;
  onHide: () => void;
}

// Write one outgoing report's file now, for a period of whole UTC days that have ended (the server
// refuses today). Mount it when it opens, so the dates start every time at the week the latest Tuesday
// report covers — the week the weekly job sends (CLE-2966).
export function SendNowDialog({
  partner,
  report,
  onHide,
}: ISendNowDialogProps) {
  const toastRef = useContext(ToastContext);
  const [run, { isLoading }] = useRunSftpOutboundReportMutation();
  const yesterday = useMemo(() => yesterdayUtc(), []);
  const maxDate = useMemo(() => isoDateToLocalDate(yesterday), [yesterday]);
  const week = useMemo(() => lastReportedWeekUtc(), []);
  const [dateFrom, setDateFrom] = useState(week.dateFrom);
  const [dateTo, setDateTo] = useState(week.dateTo);

  // ISO strings compare correctly as text.
  let error: string | null = null;
  if (!dateFrom || !dateTo) error = "Choose both dates.";
  else if (dateFrom > dateTo) error = "From must be on or before To.";
  else if (dateTo > yesterday)
    error = `The file can only cover days that have ended (UTC): ${yesterday} at the latest.`;
  const canSend = !isLoading && !error;

  const fileName =
    dateFrom && dateTo
      ? outboundFileName(report.fileNamePrefix, dateFrom, dateTo)
      : "";

  const onDate = (setter: (v: string) => void) => (e: { value?: unknown }) =>
    setter(e.value instanceof Date ? localDateToIsoDate(e.value) : "");

  const onSend = async () => {
    if (!canSend) return;
    try {
      const result = await run({
        slug: partner.slug,
        id: report.id,
        dateFrom,
        dateTo,
      }).unwrap();
      const { rowCount } = result.data;
      toastRef?.current?.show({
        severity: "success",
        summary: "File sent",
        detail: `Sent ${result.data.fileName} (${rowCount} ${
          rowCount === 1 ? "row" : "rows"
        })`,
        life: 8000,
      });
      onHide();
    } catch (err) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Couldn't send the file",
        detail: sftpApiErrorMessage(err, "Please try again."),
        life: 10000,
      });
    }
  };

  const footer = (
    <div className="flex gap-2 justify-content-end">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isLoading}
      />
      <Button
        type="button"
        label="Send"
        icon="pi pi-send"
        loading={isLoading}
        disabled={!canSend}
        onClick={onSend}
        data-testid="sftp-send-now-submit"
      />
    </div>
  );

  return (
    <Dialog
      visible
      header="Send now"
      footer={footer}
      onHide={onHide}
      style={{ width: "560px", maxWidth: "95vw" }}
    >
      <div className="flex flex-column gap-3">
        <div className="text-600 text-sm">
          Days are UTC calendar days, the same as Date Paid on the dashboard.
          The latest day you can send is yesterday ({yesterday}).
        </div>
        <div className="grid">
          <div className="col-12 md:col-6">
            <label
              htmlFor="sftp-send-from"
              className="block font-semibold mb-2"
            >
              From
            </label>
            <Calendar
              inputId="sftp-send-from"
              value={dateFrom ? isoDateToLocalDate(dateFrom) : null}
              onChange={onDate(setDateFrom)}
              dateFormat="yy-mm-dd"
              maxDate={maxDate}
              disabled={isLoading}
              className="w-full"
              appendTo={document.body}
            />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="sftp-send-to" className="block font-semibold mb-2">
              To
            </label>
            <Calendar
              inputId="sftp-send-to"
              value={dateTo ? isoDateToLocalDate(dateTo) : null}
              onChange={onDate(setDateTo)}
              dateFormat="yy-mm-dd"
              maxDate={maxDate}
              disabled={isLoading}
              className="w-full"
              appendTo={document.body}
            />
          </div>
        </div>
        <div className="text-600 text-sm">
          The dates default to the Monday-to-Sunday week the latest Tuesday
          report covers, the same week the weekly job sends.
        </div>
        <div>
          <div className="text-600 text-sm mb-1">File</div>
          <code data-testid="sftp-send-now-file-name">
            {`${partner.s3Prefix}${report.folder}${fileName}`}
          </code>
        </div>
        {error ? (
          <small className="p-error" data-testid="sftp-send-now-error">
            {error}
          </small>
        ) : null}
        <Message
          severity="warn"
          text="If a file for this period already exists it is replaced."
        />
        {!report.enabled ? (
          <Message
            severity="info"
            text="This report is disabled. Sending a file now doesn't enable it."
          />
        ) : null}
      </div>
    </Dialog>
  );
}
